/**
 * 홈페이지 API 서비스
 */
import { apiClient } from '../../../core/api/client';
import { getUserInfo } from '../../../core/utils';
import {
    HomePageDeliveryCountResponse,
    HomePageDeliveryCountRequest
} from '../types/homePage';

const BASE_PATH = '/api/v1/mob/homepage';

const DEFAULT_SLOT_NAMES = ['아침/점심 피크', '오후 논피크', '저녁 피크', '야간/심야'];
const DEFAULT_SLOT_TIME_RANGES = [
    '평일: 06:00 ~ 12:59\n주말: 06:00 ~ 13:59',
    '평일: 13:00 ~ 16:59\n주말: 14:00 ~ 16:59',
    '평일: 17:00 ~ 19:59\n평일/주말 동일',
    '평일: 20:00 ~ 02:59\n평일/주말 동일',
];

export interface RiderSlotData {
    slotNames: string[];
    slotCounts: number[];
    slotTimeRanges: string[];
}

/**
 * 라이더의 슬롯별 수행 건수 조회
 * - 슬롯명: GET /api/v1/mob/dashboard/weekly -> supplierPerformances[0].slotNames
 * - 수행 건수: GET /api/v1/mob/dashboard/weekly/riders -> riderPerformances[riderId].dailySlots
 * - toDate를 전달하면 fromDate~toDate 기간의 dailySlots 합산, 생략 시 단일 date 조회
 */
export const getRiderSlotData = async (date: string, toDate?: string): Promise<RiderSlotData> => {
    try {
        const adminViewRiderId = localStorage.getItem('ADMIN_VIEW_RIDER_ID');
        const currentRiderId = adminViewRiderId || getUserInfo()?.riderId || '';
        const endDate = toDate ?? date;
        const params = {fromDate: date, toDate: endDate};

        const [weeklyResult, ridersResult] = await Promise.allSettled([
            apiClient.get<{
                supplierPerformances?: Array<{
                    slotNames: string[];
                    slotTimeRanges?: string[];
                    dailySlotTimeRanges?: Record<string, string[]>;
                }>;
                success: boolean;
            }>('/api/v1/mob/dashboard/weekly', { params }),
            apiClient.get<{
                riderPerformances: Array<{
                    riderId: string;
                    dailySlots: Record<string, number[]>;
                }>;
                success: boolean;
            }>('/api/v1/mob/dashboard/weekly/riders', { params }),
        ]);

        if (weeklyResult.status === 'rejected') {
            console.error('[getRiderSlotData] weekly API 실패:', weeklyResult.reason);
        }
        if (ridersResult.status === 'rejected') {
            console.error('[getRiderSlotData] riders API 실패:', ridersResult.reason);
        }

        const weeklyData = weeklyResult.status === 'fulfilled' ? weeklyResult.value.data : null;
        const ridersData = ridersResult.status === 'fulfilled' ? ridersResult.value.data : null;

        const suppPerf = weeklyData?.supplierPerformances?.[0];
        const slotNames: string[] = suppPerf?.slotNames?.length ? suppPerf.slotNames : DEFAULT_SLOT_NAMES;
        const slotTimeRanges: string[] =
            (suppPerf?.dailySlotTimeRanges?.[date]?.length ? suppPerf.dailySlotTimeRanges![date] : null)
            ?? (suppPerf?.slotTimeRanges?.length ? suppPerf.slotTimeRanges : null)
            ?? slotNames.map((_, idx) => DEFAULT_SLOT_TIME_RANGES[idx] ?? '');

        const riderPerf = ridersData?.riderPerformances?.find(r => r.riderId === currentRiderId);

        let slotCounts: number[];
        if (toDate && toDate !== date) {
            // 주간 범위: 기간 내 모든 날짜의 dailySlots 합산
            const allDailySlots = Object.values(riderPerf?.dailySlots ?? {});
            slotCounts = slotNames.map((_, idx) =>
                allDailySlots.reduce((sum, daySlots) => sum + (daySlots[idx] ?? 0), 0)
            );
        } else {
            // 단일 날짜
            const dateSlots: number[] = riderPerf?.dailySlots?.[date] ?? [];
            slotCounts = slotNames.map((_, idx) => dateSlots[idx] ?? 0);
        }

        return { slotNames, slotCounts, slotTimeRanges };
    } catch (e) {
        console.error('[getRiderSlotData] 예상치 못한 오류:', e);
        return {
            slotNames: DEFAULT_SLOT_NAMES,
            slotCounts: [0, 0, 0, 0],
            slotTimeRanges: [...DEFAULT_SLOT_TIME_RANGES],
        };
    }
};

/**
 * 홈페이지 배송 건수 조회
 * GET /api/v1/mob/homepage/delivery-list
 */
export const getHomePageDeliveryList = async (
  params: HomePageDeliveryCountRequest
): Promise<HomePageDeliveryCountResponse> => {
  const adminViewRiderId = localStorage.getItem('ADMIN_VIEW_RIDER_ID');
  const requestParams = adminViewRiderId 
    ? { ...params, riderId: adminViewRiderId }
    : params;
    
  const response = await apiClient.get<HomePageDeliveryCountResponse>(
    `${BASE_PATH}/home-delivery-list`,
    { params: requestParams }
  );
  return response.data;
};

